import { createAsyncThunk } from '@reduxjs/toolkit';
import { PartsService } from '../../../epics/region_services/parts';
import { PartsAllService } from '../../../epics/region_services/parts/all';
import {
    addPartsKey,
    deletePartsKey,
    editPartsKey,
    getPartsKey,
    getPartsListKey,
    partsAllKey,
    partsAllListKey,
    partsNoAccountKey,
    partsNoAccountListKey,
    updatePartsKey,
} from './partsKeys';
import { clearPartsKey } from '../../common/thunkKeys/partsThunksKeys';
import { clearAccessoryParts } from '../../common/parts_reducers/accessoryParts';
import { clearBrakesParts } from '../../common/parts_reducers/brakesParts';
import { clearElectricParts } from '../../common/parts_reducers/electricParts';
import { clearEngineParts } from '../../common/parts_reducers/engineParts';
import { clearEquipmentParts } from '../../common/parts_reducers/equipmentParts';
import { clearGasEquipmentParts } from '../../common/parts_reducers/gasEquipmentParts';
import { clearHeatingParts } from '../../common/parts_reducers/heatingParts';
import { clearInteriorParts } from '../../common/parts_reducers/interiorParts';
import { clearMaintenanceParts } from '../../common/parts_reducers/maintenanceParts';
import { clearNameParts } from '../../common/parts_reducers/nameParts';
import { clearStateParts } from '../../common/parts_reducers/stateParts';
import { clearSuspensionParts } from '../../common/parts_reducers/suspensionParts';
import { clearTransmissionParts } from '../../common/parts_reducers/transmissionParts';
import { clearTypeParts } from '../../common/parts_reducers/typeParts';
import { clearTyreSeasonParts } from '../../common/parts_reducers/tyreSeasonParts';
import { clearWheelsBoltsParts } from '../../common/parts_reducers/wheelsBoltsParts';
import { clearWheelsParts } from '../../common/parts_reducers/wheelsParts';
import { clearWheelsRadiusParts } from '../../common/parts_reducers/wheelsRadiusParts';
import { clearWheelsWidthParts } from '../../common/parts_reducers/wheelsWidthParts';
import {
    getAccessoryParts,
    getBrakesParts,
    getCarbodyParts,
    getElectricParts,
    getEngineParts,
    getEquipmentParts,
    getGasEquipmentParts,
    getHeatingParts,
    getInteriorParts,
    getMaintenanceParts,
    getNameParts,
    getStateParts,
    getSuspensionParts,
    getTransmissionParts,
    getTypeParts,
    getTyreSeasonParts,
    getWheelsBoltsParts,
    getWheelsParts,
    getWheelsRadiusParts,
    getWheelsWidthParts,
} from '../../common/thunks/partsThunks';

export const addParts = createAsyncThunk(
    addPartsKey,
    async (values, thunkApi) => {
        try {
            const response = await PartsService.addParts(values);
            return response;
        } catch (error) {
            return thunkApi.rejectWithValue({
                message: error?.message || error?.error || 'unknown',
                details: error?.details || null,
            });
        }
    },
);

export const getParts = createAsyncThunk(getPartsKey, async (id, thunkApi) => {
    try {
        const response = await PartsService.getParts(id);
        return response;
    } catch (error) {
        return thunkApi.rejectWithValue(error.message);
    }
});

export const getPartsList = createAsyncThunk(
    getPartsListKey,
    async (payload, thunkApi) => {
        try {
            const response = await PartsService.getPartsList(payload);
            return response;
        } catch (error) {
            return thunkApi.rejectWithValue(error.message);
        }
    },
);

export const deleteParts = createAsyncThunk(
    deletePartsKey,
    async (id, thunkApi) => {
        try {
            const response = await PartsService.deleteParts(id);
            return response;
        } catch (error) {
            return thunkApi.rejectWithValue(error.message);
        }
    },
);

export const editParts = createAsyncThunk(
    editPartsKey,
    async (updates, thunkApi) => {
        try {
            const response = await PartsService.editParts(updates);
            return response;
        } catch (error) {
            return thunkApi.rejectWithValue(error.message);
        }
    },
);

export const updateParts = createAsyncThunk(
    updatePartsKey,
    async (updates, thunkApi) => {
        try {
            const response = await PartsService.updateParts(updates);
            return response;
        } catch (error) {
            return thunkApi.rejectWithValue(error.message);
        }
    },
);

export const getPartsAllList = createAsyncThunk(
    partsAllListKey,
    async (payload, thunkApi) => {
        try {
            const response = await PartsAllService.getPartsAllList(payload);
            return response;
        } catch (error) {
            return thunkApi.rejectWithValue(error.message);
        }
    },
);

export const getPartsAll = createAsyncThunk(
    partsAllKey,
    async (id, thunkApi) => {
        try {
            const response = await PartsAllService.getPartsAll(id);
            return response;
        } catch (error) {
            return thunkApi.rejectWithValue(error.message);
        }
    },
);

export const getPartsNoAccountList = createAsyncThunk(
    partsNoAccountListKey,
    async (payload, thunkApi) => {
        try {
            const response =
                await PartsAllService.getPartsNoAccountList(payload);
            return response;
        } catch (error) {
            return thunkApi.rejectWithValue(error.message);
        }
    },
);

export const getPartsNoAccount = createAsyncThunk(
    partsNoAccountKey,
    async (id, thunkApi) => {
        try {
            const response = await PartsAllService.getPartsNoAccount(id);
            return response;
        } catch (error) {
            return thunkApi.rejectWithValue(error.message);
        }
    },
);

export const clearPartsDataAction = createAsyncThunk(
    clearPartsKey,
    (_, { dispatch }) => {
        dispatch(clearAccessoryParts());
        dispatch(clearBrakesParts());
        dispatch(clearElectricParts());
        dispatch(clearEngineParts());
        dispatch(clearEquipmentParts());
        dispatch(clearGasEquipmentParts());
        dispatch(clearHeatingParts());
        dispatch(clearInteriorParts());
        dispatch(clearMaintenanceParts());
        dispatch(clearNameParts());
        dispatch(clearStateParts());
        dispatch(clearSuspensionParts());
        dispatch(clearTransmissionParts());
        dispatch(clearTypeParts());
        dispatch(clearTyreSeasonParts());
        dispatch(clearWheelsBoltsParts());
        dispatch(clearWheelsParts());
        dispatch(clearWheelsRadiusParts());
        dispatch(clearWheelsWidthParts());

        dispatch(getAccessoryParts());
        dispatch(getBrakesParts());
        dispatch(getCarbodyParts());
        dispatch(getElectricParts());
        dispatch(getEngineParts());
        dispatch(getEquipmentParts());
        dispatch(getGasEquipmentParts());
        dispatch(getHeatingParts());
        dispatch(getInteriorParts());
        dispatch(getMaintenanceParts());
        dispatch(getNameParts());
        dispatch(getStateParts());
        dispatch(getSuspensionParts());
        dispatch(getTransmissionParts());
        dispatch(getTypeParts());
        dispatch(getTyreSeasonParts());
        dispatch(getWheelsBoltsParts());
        dispatch(getWheelsParts());
        dispatch(getWheelsRadiusParts());
        dispatch(getWheelsWidthParts());
    },
);
